/**
 * Popover — Positioned floating content anchored to a trigger element.
 *
 * Popover Component Guidelines
 * - Use Popover for contextual content richer than a Tooltip but lighter than a
 *   Modal — small forms, options panels, or previews that don't need full-screen focus.
 * - Prefer Modal when the content requires the user's full attention or blocks the
 *   main UI. Prefer Tooltip when content is a brief, non-interactive label.
 * - Set `triggerAsChild` when the trigger is already an interactive element (e.g.
 *   `<Button>`) to avoid wrapping it in an extra `<button>`.
 * - Use `showCloseButton={false}` for lightweight popovers where clicking outside
 *   is the natural dismiss pattern (e.g. color pickers, emoji pickers).
 * - Prefer the simple `<Popover>` for standard title/description/footer layouts.
 *   Switch to the composable API (`PopoverRoot` / `PopoverContent` / etc.) only
 *   when you need full control over the internal structure.
 * @see https://base-ui.com/react/components/popover Base UI Popover documentation
 * @see https://ui.shadcn.com/docs/components/popover shadcn/ui Popover documentation
 */
import type { LayoutClassName } from "@replohq/lattice/types/layoutClassName";

import React from "react";

import { Popover as PopoverPrimitive } from "@base-ui/react/popover";
import { Button } from "@replohq/lattice/components/button/Button";
import {
  PortalThemeScope,
  usePortalContainer,
} from "@replohq/lattice/components/portal-theme/PortalTheme";
import { cn } from "@replohq/lattice/utils/cn";
import { XIcon } from "lucide-react";

function PopoverRoot({ ...props }: PopoverPrimitive.Root.Props) {
  return <PopoverPrimitive.Root data-slot="popover" {...props} />;
}

interface PopoverTriggerProps<ClassName extends string = string>
  extends Omit<PopoverPrimitive.Trigger.Props, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

const PopoverTriggerInner = <ClassName extends string>(
  { className, UNSAFE_className, ...props }: PopoverTriggerProps<ClassName>,
  ref: React.ForwardedRef<HTMLButtonElement>,
) => {
  // NOTE (Ben, 2026-02-10): Only pass className if there's an actual value.
  // When using render prop, Base UI merges props onto the child.
  // If we pass className="" (from twMerge with no inputs), it overrides the child's
  // own className, breaking styles.
  const mergedClassName = cn(className, UNSAFE_className);

  return (
    <PopoverPrimitive.Trigger
      ref={ref}
      data-slot="popover-trigger"
      {...(mergedClassName ? { className: mergedClassName } : {})}
      {...props}
    />
  );
};

// Legacy cast - may or may not be necessary
// eslint-disable-next-line replo/no-as-cast
const PopoverTrigger = React.forwardRef(PopoverTriggerInner) as <
  ClassName extends string,
>(
  props: PopoverTriggerProps<ClassName> & {
    ref?: React.ForwardedRef<HTMLButtonElement>;
  },
) => React.ReactElement | null;

interface PopoverContentProps<ClassName extends string = string>
  extends Omit<PopoverPrimitive.Popup.Props, "className">,
    Pick<
      PopoverPrimitive.Positioner.Props,
      "align" | "alignOffset" | "side" | "sideOffset" | "anchor"
    > {
  showCloseButton?: boolean;
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
  /**
   * Override the Positioner's tailwind class. Use to bump the stacking order
   * when the popover renders alongside a high-z-index overlay (e.g. lattice's
   * default `z-[100]` is too low for portaled overlays running at z 99999+).
   */
  UNSAFE_positionerClassName?: string;
  /**
   * Override the `<Portal>` target. Defaults to `document.body`. Pass a ref
   * (or element) when the popover lives inside an overlay that tracks "click
   * outside" via DOM containment — keeping the portal inside the same subtree
   * means outside-click detectors see the popover as "inside" without needing
   * to special-case portaled content.
   */
  UNSAFE_portalContainer?: PopoverPrimitive.Portal.Props["container"];
}

function PopoverContent<ClassName extends string>({
  align = "center",
  alignOffset = 0,
  side = "bottom",
  sideOffset = 4,
  showCloseButton = true,
  anchor,
  children,
  className,
  UNSAFE_className,
  UNSAFE_positionerClassName,
  UNSAFE_portalContainer,
  ...props
}: PopoverContentProps<ClassName>) {
  const container = usePortalContainer();
  const positioner = (
    <PopoverPrimitive.Positioner
      align={align}
      alignOffset={alignOffset}
      side={side}
      sideOffset={sideOffset}
      anchor={anchor}
      className={cn("isolate z-[100]", UNSAFE_positionerClassName)}
    >
      <PopoverPrimitive.Popup
        data-slot="popover-content"
        className={cn(
          "bg-sheet text-default data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 border border-border border-0.5 flex flex-col gap-3 rounded-2xl p-4 text-sm shadow-md duration-100 w-72 origin-(--transform-origin) outline-hidden",
          className,
          UNSAFE_className,
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <PopoverPrimitive.Close
            data-slot="popover-close"
            render={
              <Button
                variant="tertiary"
                icon
                size="sm"
                className="absolute top-4 right-4"
              />
            }
          >
            <XIcon />
            <span className="sr-only">Close</span>
          </PopoverPrimitive.Close>
        )}
      </PopoverPrimitive.Popup>
    </PopoverPrimitive.Positioner>
  );

  return (
    <PopoverPrimitive.Portal container={UNSAFE_portalContainer ?? container}>
      <PortalThemeScope>{positioner}</PortalThemeScope>
    </PopoverPrimitive.Portal>
  );
}

function PopoverClose({ ...props }: PopoverPrimitive.Close.Props) {
  return <PopoverPrimitive.Close data-slot="popover-close" {...props} />;
}

interface PopoverHeaderProps<ClassName extends string = string>
  extends Omit<React.ComponentProps<"div">, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function PopoverHeader<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: PopoverHeaderProps<ClassName>) {
  return (
    <div
      data-slot="popover-header"
      className={cn("flex flex-col gap-1 text-sm", className, UNSAFE_className)}
      {...props}
    />
  );
}

interface PopoverTitleProps<ClassName extends string = string>
  extends Omit<PopoverPrimitive.Title.Props, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function PopoverTitle<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: PopoverTitleProps<ClassName>) {
  return (
    <PopoverPrimitive.Title
      data-slot="popover-title"
      className={cn("text-default header-h5", className, UNSAFE_className)}
      {...props}
    />
  );
}

interface PopoverDescriptionProps<ClassName extends string = string>
  extends Omit<PopoverPrimitive.Description.Props, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function PopoverDescription<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: PopoverDescriptionProps<ClassName>) {
  return (
    <PopoverPrimitive.Description
      data-slot="popover-description"
      className={cn("text-muted", className, UNSAFE_className)}
      {...props}
    />
  );
}

interface PopoverFooterProps<ClassName extends string = string>
  extends Omit<React.ComponentProps<"div">, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function PopoverFooter<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: PopoverFooterProps<ClassName>) {
  return (
    <div
      data-slot="popover-footer"
      className={cn(
        "flex flex-row gap-3 justify-end",
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
}

export type PopoverProps<ClassName extends string = string> =
  React.PropsWithChildren<{
    title?: string;
    description?: string;
    trigger?: React.ReactNode;
    /**
     * When true, the trigger element itself becomes the popover trigger
     * (via Base UI's render prop) instead of being wrapped in a default `<button>`.
     * Use this when `trigger` is already an interactive element like `<Button>`.
     * @default false
     */
    triggerAsChild?: boolean;
    footer?: React.ReactNode;
    open?: boolean;
    onOpenChange?: (
      open: boolean,
      eventDetails: PopoverPrimitive.Root.ChangeEventDetails,
    ) => void;
    /**
     * Whether to show the close button in the top-right corner.
     * @default true
     */
    showCloseButton?: boolean;
    side?: PopoverContentProps["side"];
    sideOffset?: PopoverContentProps["sideOffset"];
    align?: PopoverContentProps["align"];
    alignOffset?: PopoverContentProps["alignOffset"];
    /**
     * Layout class name applied to the popover content.
     */
    className?: LayoutClassName<ClassName>;
    /**
     * Escape hatch for non-layout classes on the popover content.
     */
    UNSAFE_className?: string;
  }>;

function Popover<ClassName extends string>({
  title,
  description,
  trigger,
  triggerAsChild = false,
  footer,
  children,
  open,
  onOpenChange,
  showCloseButton = true,
  side,
  sideOffset,
  align,
  alignOffset,
  className,
  UNSAFE_className,
}: PopoverProps<ClassName>) {
  const hasHeader = title || description;

  return (
    <PopoverRoot open={open} onOpenChange={onOpenChange}>
      {trigger &&
        (triggerAsChild ? (
          <PopoverTrigger
            // Legacy cast - may or may not be necessary
            // eslint-disable-next-line replo/no-as-cast
            render={trigger as React.ReactElement<Record<string, unknown>>}
          />
        ) : (
          <PopoverTrigger>{trigger}</PopoverTrigger>
        ))}
      <PopoverContent
        showCloseButton={showCloseButton}
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        className={className}
        UNSAFE_className={UNSAFE_className}
      >
        {hasHeader && (
          <PopoverHeader>
            {title && <PopoverTitle>{title}</PopoverTitle>}
            {description && (
              <PopoverDescription>{description}</PopoverDescription>
            )}
          </PopoverHeader>
        )}

        {/* NOTE (Ben, 2026-02-10): If there's no header and the close button is shown,
        we need to add a placeholder span to ensure the content doesn't overlap with the close button. */}
        {!hasHeader && showCloseButton ? (
          <div className="w-full flex justify-between gap-3">
            {children}
            <span aria-hidden="true" className="size-6" />
          </div>
        ) : (
          children
        )}

        {footer && <PopoverFooter>{footer}</PopoverFooter>}
      </PopoverContent>
    </PopoverRoot>
  );
}

export {
  Popover,
  PopoverRoot,
  PopoverTrigger,
  PopoverContent,
  PopoverClose,
  PopoverHeader,
  PopoverFooter,
  PopoverTitle,
  PopoverDescription,
  // eslint-disable-next-line replo/no-export-from
  // eslint-disable-next-line replo/no-export-from
  PopoverPrimitive,
};
